import React, { useState, useEffect, useContext } from 'react';

/** @jsx jsx */
import { css, jsx } from '@emotion/core';

import { Event, CoreComponent, ReactWrapper } from '../../Docs/DocTypes';
import { Code } from './Code';
import { getKnobValues, parseCodeWithKnobs, KnobValues } from './KnobsUtils';

import { RunningHtmlCode } from '../RunningHtmlCode';
import { RunningReactCode } from '../RunningReactCode';
import { CodeTab } from './CodeTab';
import { darkModeContext } from '../../darkModeContext';

type Props = {
  coreComponent: CoreComponent;
  coreComponent_v3: CoreComponent;
  reactWrapper: ReactWrapper;
  reactWrapper_v3: ReactWrapper;
  events: Event[];
  codeVisible?: boolean;
  mobileFriendly: boolean;
  mobileView: boolean;
  onCodeVisibleChange?: (newCodeVisibleChange: boolean) => void;
  codeBackgroundVisible?: boolean;
  onCodeBackgroundVisibleChange?: (newbackgroundVisibleChange: boolean) => void;
  codeTab?: string;
  onMobileViewChange: (newMobileView: boolean) => void;
};
export const CodePreview = (props: Props) => {
  const defaultKnobs = {
    html: getKnobValues(props.coreComponent.knobs, props.coreComponent.knobInitialValues),
    jsx: getKnobValues(props.reactWrapper.knobs, props.reactWrapper.knobInitalValues),
  };
  const [htmlKnobValues, setHtmlKnobValues] = useState(defaultKnobs.html);
  const [htmlKnobValuesV3, setHtmlKnobValuesV3] = useState(defaultKnobs.html);
  const [reactKnobValues, setReactKnobValues] = useState(defaultKnobs.jsx);
  const [reactKnobValuesV3, setReactKnobValuesV3] = useState(defaultKnobs.jsx);

  const docsDarkMode = useContext(darkModeContext);
  const docsDarkModeValue = docsDarkMode.value || false;

  useEffect(() => {
    if (props.coreComponent.knobs && Object.keys(props.coreComponent.knobs).length > 0) {
      setHtmlKnobValues(
        getKnobValues(props.coreComponent.knobs, props.coreComponent.knobInitialValues),
      );
    }
  }, [props.coreComponent.knobs, props.coreComponent.knobInitialValues]);

  useEffect(() => {
    if (props.coreComponent_v3.knobs && Object.keys(props.coreComponent_v3.knobs).length > 0) {
      setHtmlKnobValuesV3(
        getKnobValues(props.coreComponent_v3.knobs, props.coreComponent_v3.knobInitialValues),
      );
    }
  }, [props.coreComponent_v3.knobs, props.coreComponent_v3.knobInitialValues]);

  useEffect(() => {
    if (props.reactWrapper.knobs && Object.keys(props.reactWrapper.knobs).length > 0) {
      setReactKnobValues(
        getKnobValues(props.reactWrapper.knobs, props.reactWrapper.knobInitalValues),
      );
    }
  }, [props.reactWrapper.knobs, props.reactWrapper.knobInitalValues]);

  useEffect(() => {
    if (props.reactWrapper_v3.knobs && Object.keys(props.reactWrapper_v3.knobs).length > 0) {
      setReactKnobValuesV3(
        getKnobValues(props.reactWrapper_v3.knobs, props.reactWrapper_v3.knobInitalValues),
      );
    }
  }, [props.reactWrapper_v3.knobs, props.reactWrapper_v3.knobInitalValues]);

  const handleCodeVisibleChange = (newCodeVisibleChange: boolean) => {
    if (props.onCodeVisibleChange) {
      props.onCodeVisibleChange(newCodeVisibleChange);
    }
  };

  const handleCodeBackgroundVisibleChange = (newBackgroundVisibleChange: boolean) => {
    if (props.onCodeBackgroundVisibleChange) {
      props.onCodeBackgroundVisibleChange(newBackgroundVisibleChange);
    }
  };

  const handleMobileViewChange = (newMobileViewChange: boolean) => {
    if (props.onMobileViewChange) {
      props.onMobileViewChange(newMobileViewChange);
    }
  };

  const styles = makeStyles(docsDarkModeValue);
  return (
    <div css={styles.root}>
      {props.codeTab &&
        props.codeTab.toLowerCase() === 'html' &&
        renderCoreComponent(
          props.coreComponent,
          props.events,
          props.codeVisible,
          props.mobileFriendly,
          props.mobileView,
          props.codeBackgroundVisible,
          htmlKnobValues,
          handleCodeVisibleChange,
          handleCodeBackgroundVisibleChange,
          handleMobileViewChange,
          setHtmlKnobValues,
        )}
      {props.codeTab &&
        props.coreComponent_v3 &&
        props.codeTab.toLowerCase() === 'html_v3' &&
        renderCoreComponent(
          props.coreComponent_v3,
          props.events,
          props.codeVisible,
          props.mobileFriendly,
          props.mobileView,
          props.codeBackgroundVisible,
          htmlKnobValuesV3,
          handleCodeVisibleChange,
          handleCodeBackgroundVisibleChange,
          handleMobileViewChange,
          setHtmlKnobValuesV3,
          true,
        )}
      {props.codeTab &&
        props.codeTab.toLowerCase() === 'react' &&
        renderReactWrapper(
          props.reactWrapper,
          props.events,
          reactKnobValues,
          props.mobileFriendly,
          props.mobileView,
          props.codeBackgroundVisible,
          props.codeVisible,
          getReactCode(props.reactWrapper),
          handleCodeVisibleChange,
          handleCodeBackgroundVisibleChange,
          handleMobileViewChange,
          setReactKnobValues,
        )}
      {props.codeTab &&
        props.codeTab.toLowerCase() === 'react_v3' &&
        renderReactWrapper(
          props.reactWrapper_v3,
          props.events,
          reactKnobValuesV3,
          props.mobileFriendly,
          props.mobileView,
          props.codeBackgroundVisible,
          props.codeVisible,
          getReactCode(props.reactWrapper_v3),
          handleCodeVisibleChange,
          handleCodeBackgroundVisibleChange,
          handleMobileViewChange,
          setReactKnobValuesV3,
          true,
        )}
    </div>
  );
};

function getReactCode(reactWrapper: ReactWrapper) {
  return reactWrapper.parentJSX
    ? `${reactWrapper.parentJSX}\n${reactWrapper.jsx || ''}`
    : `${reactWrapper.jsx || ''}`;
}

function renderReactWrapper(
  reactWrapper: ReactWrapper,
  events: Event[],
  reactKnobValues: KnobValues,
  mobileFriendly: boolean,
  mobileView: boolean,
  codeBackgroundVisible: boolean = false,
  codeVisible: boolean = false,
  reactCode: string,
  handleCodeVisibleChange: (newCodeVisibleChange: boolean) => void,
  handleCodeBackgroundVisibleChange: (newBackgroundVisibleChange: boolean) => void,
  handleMobileViewChange: (newMobileViewChange: boolean) => void,
  setReactKnobValues: React.Dispatch<React.SetStateAction<KnobValues>>,
  isV3?: boolean,
): React.ReactNode {
  return (
    <CodeTab
      renderRunningCode={() => (
        <RunningReactCode
          parentJSX={reactWrapper.parentJSX || ''}
          jsx={reactWrapper.jsx || ''}
          knobs={reactWrapper.knobs}
          knobValues={reactKnobValues}
          events={concatEvents(events, reactWrapper.additionalEvents)}
          backgroundVisible={codeBackgroundVisible}
          isV3={isV3}
          mobileView={mobileView && mobileFriendly}
        />
      )}
      renderCode={() => (
        <Code
          className="react-code"
          language="jsx"
          code={parseCodeWithKnobs(reactCode, reactWrapper.knobs, reactKnobValues, false)}
        ></Code>
      )}
      codeVisible={codeVisible ? codeVisible : false}
      onCodeVisibleChange={handleCodeVisibleChange}
      backgroundVisible={codeBackgroundVisible ? codeBackgroundVisible : false}
      onBackgroundVisibleChange={handleCodeBackgroundVisibleChange}
      knobs={reactWrapper.knobs ? reactWrapper.knobs : {}}
      knobValues={reactKnobValues}
      onKnobValuesChange={newValues => setReactKnobValues(newValues)}
      camelCaseLabels={true}
      mobileFriendly={mobileFriendly}
      mobileView={mobileView}
      onMobileViewChange={handleMobileViewChange}
    />
  );
}

function renderCoreComponent(
  coreComponent: CoreComponent,
  events: Event[],
  codeVisible: boolean | undefined,
  mobileFriendly: boolean,
  mobileView: boolean,
  codeBackgroundVisible: boolean | undefined,
  htmlKnobValues: KnobValues,
  handleCodeVisibleChange: (newCodeVisibleChange: boolean) => void,
  handleCodeBackgroundVisibleChange: (newBackgroundVisibleChange: boolean) => void,
  handleMobileViewChange: (newMobileViewChange: boolean) => void,
  setHtmlKnobValues: React.Dispatch<React.SetStateAction<KnobValues>>,
  isV3?: boolean,
): React.ReactNode {
  return (
    <CodeTab
      renderRunningCode={() => (
        <RunningHtmlCode
          html={coreComponent.html}
          events={concatEvents(events, coreComponent.additionalEvents)}
          js={coreComponent.js || ''}
          knobs={coreComponent.knobs}
          knobValues={htmlKnobValues}
          backgroundVisible={codeBackgroundVisible}
          isV3={isV3}
          mobileView={mobileView && mobileFriendly}
        />
      )}
      renderCode={() => (
        <React.Fragment>
          {coreComponent.html && (
            <Code
              heading={coreComponent.html && coreComponent.js ? 'html' : ''}
              code={parseCodeWithKnobs(
                coreComponent.html,
                coreComponent.knobs,
                htmlKnobValues,
                true,
              )}
            ></Code>
          )}
          {coreComponent.js && (
            <Code
              heading={coreComponent.html && coreComponent.js ? 'js' : ''}
              language="js"
              code={coreComponent.js}
            ></Code>
          )}
        </React.Fragment>
      )}
      codeVisible={codeVisible ? codeVisible : false}
      onCodeVisibleChange={handleCodeVisibleChange}
      backgroundVisible={codeBackgroundVisible ? codeBackgroundVisible : false}
      onBackgroundVisibleChange={handleCodeBackgroundVisibleChange}
      knobs={coreComponent.knobs ? coreComponent.knobs : {}}
      knobValues={htmlKnobValues}
      onKnobValuesChange={newValues => setHtmlKnobValues(newValues)}
      camelCaseLabels={false}
      mobileFriendly={mobileFriendly}
      mobileView={mobileView}
      onMobileViewChange={handleMobileViewChange}
    />
  );
}

function concatEvents(htmlEvents?: Event[], htmlAdditionalEvents?: string[]): string[] | undefined {
  if (!htmlEvents) return htmlAdditionalEvents;
  if (!htmlAdditionalEvents) return htmlEvents.map(e => e.name);
  return htmlEvents.map(e => e.name).concat(htmlAdditionalEvents);
}

const makeStyles = (docsDarkModeValue: boolean) => {
  return {
    root: css`
      position: relative;
      margin: 30px 0px;
      padding: 30px;
      background: ${docsDarkModeValue ? '#000' : 'rgb(255, 255, 255)'};
      border-width: 1px;
      border-style: solid;
      border-color: rgba(0, 0, 0, 0.1);
      border-radius: 4px 4px 0px 0px;
      box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px 0px;
      max-width: 800px;
    `,
  };
};
